import logger from 'helpers/logger';
import { debounce } from 'lodash';
import React, {
    forwardRef,
    memo,
    Suspense,
    useImperativeHandle,
    useRef,
    useCallback,
    useEffect
} from 'react';
import { LoadingIndicator } from 'sharedComponents';
import { CHART_PLUGINS } from './constants';

const LazyChartComponent = React.lazy(async () => {
    const { default: Highcharts } = await import(/* webpackChunkName: "highCharts" */ 'highcharts');

    // We must allow this reference in order to have dynamic, locally created svg files on the charts
    Highcharts.AST.allowedReferences.push('data:');

    // This is a patch to keep having own SVG's embedded in the highcharts
    // if an svg does not work, please check whether all it's tags and attributes are supported or not
    Highcharts.AST.allowedAttributes.push('viewBox', 'xmlns', 'stroke-linejoin', 'x2');
    Highcharts.AST.allowedTags.push('line');
    Highcharts.seriesTypes.line.prototype.drawLegendSymbol = Highcharts.seriesTypes.column.prototype.drawLegendSymbol;

    const importers = {
        [CHART_PLUGINS.NO_DATA_TO_DISPLAY]: () => import(/* webpackChunkName: "highCharts" */ 'highcharts/modules/no-data-to-display'),
        [CHART_PLUGINS.HIGHCHARTS_MORE]: () => import(/* webpackChunkName: "highCharts" */ 'highcharts/highcharts-more'),
        [CHART_PLUGINS.BOOST]: () => import(/* webpackChunkName: "highCharts" */ 'highcharts/modules/boost'),
        [CHART_PLUGINS.EXPORTING]: () => import(/* webpackChunkName: "highCharts" */ 'highcharts/modules/exporting'),
        [CHART_PLUGINS.STOCK]: () => import(/* webpackChunkName: "highCharts" */ 'highcharts/modules/stock'),
        [CHART_PLUGINS.OFFLINE_EXPORTING]: () => import(/* webpackChunkName: "highCharts" */ 'highcharts/modules/offline-exporting'),
        [CHART_PLUGINS.ACCESSIBILITY]: () => import(/* webpackChunkName: "highCharts" */ 'highcharts/modules/accessibility'),
        'highcharts/modules/xrange': () => import(/* webpackChunkName: "highCharts" */ 'highcharts/modules/xrange'),
    };

    const InternalChart = (props, ref) => {
        const containerRef = useRef();
        const chartRef = useRef();
        const creatingChartRef = useRef(false);

        const loadModules = useCallback(async () => {
            if (!props.modules) { return; }

            for (const module of props.modules) {
                const importer = importers[module];
                if (!importer) {
                    logger.debug('Error loading highchart plugin:', module);
                    continue;
                }
                try {
                    const importedPlugin = await importer();
                    if (importedPlugin?.default) {
                        importedPlugin.default(Highcharts);
                    } else {
                        logger.debug('Error loading highchart plugin:', module);
                    }
                } catch (err) {
                    logger.debug('Error loading highchart plugin:', module, err);
                }
            }
        }, [props.modules]);

        const generateKey = (name, color) => {
            // Replacing spaces and '#' with underscores
            const normalize = str => (str || '').trim().replace(/\s+/g, '_').replace(/#/g, '_');
            return `${normalize(name)}_${normalize(color)}`;
        };

        const preserveSeriesVisibility = (options, chart) => {
            // Return original options if chart or series is missing
            if (!chart?.series) {
                return options;
            }

            // Create a map of series name to its visibility
            const visibilityMap = Object.fromEntries(chart.series.map(s => [generateKey(s.name, s.color), s.visible]));
            // Apply visibility from the map, fallback to original if not found
            options.series = options.series.map(s => ({
                ...s,
                visible: visibilityMap[generateKey(s.name, s.color)] ?? s.visible
            }));

            return options;
        };

        const createNewChart = async (debProps) => {
            creatingChartRef.current = true;
            await loadModules();
            const chartInst = new Highcharts.Chart(
                containerRef.current,
                preserveSeriesVisibility(debProps.options, chartRef.current),
                debProps.callback ? debProps.callback : undefined
            );
            chartRef.current = chartInst;
            logger.debug('Created chart', debProps.container);
            creatingChartRef.current = false;
        };

        const handleTrendSignalPopout = (debProps) => {
            if (containerRef.current && chartRef.current.renderTo !== containerRef.current) {
                const chartInst = new Highcharts.Chart(
                    containerRef.current,
                    debProps.options,
                    function () {
                        chartRef.current = this;
                    }
                );
                chartRef.current = chartInst;
            } else {
                chartRef.current.update(debProps.options, true, true, false);
            }
        };

        const updateExistingChart = (debProps) => {
            if (!chartRef?.current) {
                logger.warn('Update skipped, invalid highchart instance!');
                return;
            }

            chartRef.current.update(
                preserveSeriesVisibility(debProps.options, chartRef.current),
                true, true, false
            );

            if (debProps.showChartPopout && debProps.isTrendSignal) {
                handleTrendSignalPopout(debProps);
            }

            logger.debug('Update chart', debProps.container);
        };

        const createUpdateChart = useCallback(debounce((debProps) => {
            if (!chartRef.current && !creatingChartRef.current) {
                createNewChart(debProps);
            } else {
                updateExistingChart(debProps);
            }
        }, 100), []);

        useEffect(() => {
            return () => {
                createUpdateChart.cancel(); // cancel delayed execution
                // Destroy chart only if unmounting.
                if (chartRef.current) {
                    if (props.destroy) {
                        props.destroy(null);
                    }
                    chartRef.current?.destroy();
                    chartRef.current = null;
                    logger.debug('Chart destroyed', props.container);
                }
            };
        }, []);


        useEffect(() => {
            createUpdateChart(props);
        });

        useImperativeHandle(
            ref,
            () => ({
                get chart() {
                    return chartRef.current;
                },
                container: containerRef
            }),
            []
        );

        // Create container for the chart
        return <div {...props.containerProps} ref={containerRef} id={props.container} className='chart-style' />;
    };

    InternalChart.displayName = 'InternalChart';

    return {
        default: memo(forwardRef(InternalChart))
    };
});

const Chart = forwardRef((props, ref) => {
    return <Suspense fallback={<LoadingIndicator />}>
        <LazyChartComponent {...props} ref={ref} />
    </Suspense>;
});

Chart.displayName = 'LazyChartWrapper';

export default Chart;
